import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { COLLECTIONS } from "@/lib/collections";
import { useCollectionTexts } from "@/hooks/useCollectionTexts";

function TextEditor({ id, c, saved }) {
  const qc = useQueryClient();
  const [text, setText] = useState(saved || c.intro);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(saved || c.intro); }, [saved, c.intro]);

  const save = async (value) => {
    setBusy(true);
    try {
      await api.put(`/admin/collections/${id}/text`, { intro: value === c.intro ? "" : value });
      await qc.invalidateQueries({ queryKey: ["collection-texts"] });
      toast.success(value === c.intro ? "Texte d'origine rétabli" : "Texte de la rubrique enregistré");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`collection-text-${id}`} className="rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-2xl italic">{c.name}</h3>
        <Link to={c.path} target="_blank" data-testid={`collection-text-view-${id}`} className="flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[var(--gold-soft)] hover:underline">Voir la page <ArrowUpRight className="h-3.5 w-3.5" /></Link>
      </div>
      <p className="mt-1 text-xs text-[var(--muted)]">{saved ? "Texte personnalisé" : "Texte d'origine"} · {text.length}/2000 caractères</p>
      <textarea data-testid={`collection-text-input-${id}`} rows={6} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)}
        className="mt-4 w-full rounded-sm border border-[var(--line)] bg-transparent p-3 text-sm leading-relaxed outline-none focus:border-[var(--gold)]" />
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" data-testid={`collection-text-save-${id}`} disabled={busy || !text.trim()} onClick={() => save(text.trim())}
          className="flex h-11 items-center gap-2 rounded-full bg-[var(--gold)] px-6 text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ink)] hover:bg-[var(--gold-soft)] disabled:opacity-50">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Enregistrer
        </button>
        {saved ? (
          <button type="button" data-testid={`collection-text-reset-${id}`} disabled={busy} onClick={() => save(c.intro)}
            className="h-11 rounded-full border border-[var(--line)] px-6 text-xs uppercase tracking-[0.22em] hover:border-[var(--gold)] disabled:opacity-50">
            Rétablir le texte d'origine
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function CollectionTexts() {
  const { data } = useCollectionTexts();
  return (
    <div data-testid="admin-collection-texts" className="space-y-6">
      <p className="max-w-2xl text-sm text-[var(--muted)]">Modifiez le texte d'introduction affiché sous le titre de chaque rubrique. Il est aussi lu par Google : décrivez naturellement la gamme, les besoins couverts et la livraison. Les retours à la ligne sont conservés.</p>
      <div className="grid gap-6 lg:grid-cols-2">
        {Object.entries(COLLECTIONS).map(([id, c]) => <TextEditor key={id} id={id} c={c} saved={data?.[id]?.intro} />)}
      </div>
    </div>
  );
}
